import { useEffect } from "react";

const GA_ID = "G-0RS52QEMWX";
const STORAGE_KEY = "soxie365_consent";
export const CONSENT_EVENT = "soxie365:consent-accepted";
/** 6 months, matches the consent banner. */
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 183;

function hasAnalyticsConsent(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { choice?: string; at?: number };
    if (parsed.choice !== "accepted") return false;
    if (!parsed.at || Date.now() - parsed.at > MAX_AGE_MS) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Loads gtag.js ONLY after the visitor has accepted analytics cookies, and even
 * then only once the page is idle (or on the first interaction). Visitors who
 * reject or haven't chosen never download or execute GA, so the tag costs zero
 * main-thread time for them (and for Lighthouse/bots).
 * Consent Mode defaults are set inline in the document head.
 */
export function DeferredAnalytics() {
  useEffect(() => {
    let done = false;
    let idle: number | undefined;
    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "scroll"];

    const cleanup = () => {
      events.forEach((e) => window.removeEventListener(e, load));
      if (idle !== undefined) {
        if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
        else clearTimeout(idle);
        idle = undefined;
      }
    };

    function load() {
      if (done) return;
      done = true;
      cleanup();
      if (document.getElementById("ga-tag")) return;
      const s = document.createElement("script");
      s.id = "ga-tag";
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      document.head.appendChild(s);
    }

    const schedule = () => {
      if (done || idle !== undefined) return;
      events.forEach((e) => window.addEventListener(e, load, { once: true, passive: true }));
      idle = window.requestIdleCallback
        ? window.requestIdleCallback(load, { timeout: 6000 })
        : window.setTimeout(load, 3500);
    };

    if (hasAnalyticsConsent()) schedule();

    const onConsent = () => schedule();
    window.addEventListener(CONSENT_EVENT, onConsent);

    return () => {
      cleanup();
      window.removeEventListener(CONSENT_EVENT, onConsent);
    };
  }, []);

  return null;
}
